import { Notch } from '@/shared/ui/notch'
import { useStyles } from './styles'
import React, { useEffect, useState } from 'react'
import { useGesture } from '@use-gesture/react'
import { useHideNav } from '@/shared/hooks/useHideNav'

import { toggleScrollAllowanceFx } from '@/entities/navigation/effects'
import { usePreventScroll } from '@/shared/hooks/usePreventScroll'
import WebApp from '@twa-dev/sdk'
import { useUnit } from 'effector-react'
import { $isUserGamedPremium } from '@/entities/user'

type AppDrawerProps = {
  children: React.ReactNode
  hidden?: boolean
  notch?: boolean
}
const AppDrawer = ({ children, hidden, notch = true }: AppDrawerProps) => {
  const isUserGamedPremium = useUnit($isUserGamedPremium)

  const notchImage = !notch ? 'empty' : isUserGamedPremium ? 'alreadyplus' : 'getplus'
  // test comment

  const DRAWER_MIN_HEIGHT = 52
  const DRAWER_MAX_HEIGHT = window.innerHeight + 26

  const SNAP_POINTS = [-(DRAWER_MAX_HEIGHT - DRAWER_MIN_HEIGHT), 0]
  const OPEN_THRESHOLD = 0.4

  const [dragOffset, setDragOffset] = useState(SNAP_POINTS[0])
  const [startPosition, setStartPosition] = useState(0)
  const [initialOffset, setInitialOffset] = useState(0)

  const [isDragging, setIsDragging] = useState<boolean>(false)
  const [isOpenedEnough, setIsOpenedEnough] = useState(false)
  const isInteracting = isDragging || isOpenedEnough

  // const [isAnimating, setIsAnimating] = useState(false) // New state for tracking animation

  function handleHide() {
    // setIsAnimating(true) // Enable animation
    setDragOffset(SNAP_POINTS[0]) // Move to the hidden position
    setIsOpenedEnough(false)
    // setTimeout(() => {
    //   setIsAnimating(false) // Disable animation after it completes
    // }, 300) // Match transition duration
  }

  function handleAutoOpen() {
    // setIsAnimating(true) // Enable animation
    setDragOffset(SNAP_POINTS[1]) // Move to the fully opened position

    setIsOpenedEnough(true)
    // setTimeout(() => {
    //   setIsAnimating(false) // Disable animation after it completes
    // }, 300) // Match transition duration
  }

  useHideNav(isInteracting)
  usePreventScroll(isDragging)

  const bind = useGesture({
    onDragStart: ({ offset: [, oy] }) => {
      setIsDragging(true)
      setStartPosition(dragOffset)
      setInitialOffset(oy)
      WebApp.HapticFeedback.impactOccurred('soft')
    },
    onDrag: ({ offset: [, oy], active, first }) => {
      if (!first) {
        const currentDragOffset = oy - initialOffset
        const newOffset = Math.max(SNAP_POINTS[0], Math.min(SNAP_POINTS[1], startPosition + currentDragOffset))

        if (newOffset > dragOffset) {
          setDragOffset(newOffset)

          const totalRange = SNAP_POINTS[1] - SNAP_POINTS[0]
          const currentPosition = newOffset - SNAP_POINTS[0]
          const openPercentage = currentPosition / totalRange

          setIsOpenedEnough(openPercentage >= OPEN_THRESHOLD)

          // Check if offset exceeds breakpoint for auto-open
          if (newOffset > OPEN_THRESHOLD && !isDragging) {
            handleAutoOpen()
          }
        }
      }
      setIsDragging(active)
    },
    onDragEnd: () => {
      handleAutoOpen()
    },
  })

  useEffect(() => {
    if (isInteracting) {
      toggleScrollAllowanceFx(false)
    } else {
      toggleScrollAllowanceFx(true)
    }
  }, [isInteracting])

  const { classes } = useStyles({})
  return (
    <>
      <div
        id="appdrawer"
        style={{
          transform: `translateY(${dragOffset - (hidden ? 52 : 0)}px)`,
          height: `${DRAWER_MAX_HEIGHT}px`,
          overflow: isDragging ? 'hidden' : 'visible',
          transition: !isDragging ? 'transform 0.3s ease' : 'none', // Apply transition for animation
        }}
        className={classes.root}
      >
        <div className={classes.content}>
          {React.cloneElement(children as React.ReactElement, { handleHide, isOpenedEnough })}
        </div>

        <span
          style={{
            zIndex: 99,
          }}
          {...bind()}
          // onClick={handleAutoOpen}
        >
          <Notch variant={isOpenedEnough ? 'empty' : notchImage} />
        </span>
      </div>
      <div
        data-active={isInteracting}
        className={classes.overlay}
      />
    </>
  )
}
export default AppDrawer
